import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { MetaProvider } from "./context/MetaContext";
import { WebsiteProvider, useWebsite } from "./context/WebsiteContext";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import WordPressToolPage from "./pages/WordPressToolPage";
import WordPressToolLandingPage from "./pages/WordPressToolLandingPage";
import QuestionnairePage from "./pages/QuestionnairePage";
import IncidentsPage from "./pages/IncidentsPage";
import ReportsPage from "./pages/ReportsPage";
import PublicReportPage from "./pages/PublicReportPage";
import SettingsPage from "./pages/SettingsPage";
import PreviewPage from "./pages/PreviewPage";
import TopBar from "./components/TopBar";
import PortalHeader from "./components/PortalHeader";
import RightNavPanel from "./components/RightNavPanel";
import SiteFooter from "./components/SiteFooter";
import ToolPublicLandingPage from "./features/tools/pages/ToolPublicLandingPage";
import ToolWorkspacePlaceholderPage from "./features/tools/pages/ToolWorkspacePlaceholderPage";

const websiteNavItems = [
  { to: "/tools/wpa/dashboard/questionnaire", label: "Discussion Points", note: "Discovery and assessment capture" },
  { to: "/tools/wpa/dashboard/incidents", label: "Incidents", note: "Problems, outages, and root cause" },
  { to: "/tools/wpa/dashboard/reports", label: "Reports", note: "AI report generation and insights" }
];

const portalAreaItems = [
  { to: "/", label: "Home", note: "Platform narrative and entry points" },
  { to: "/dashboard", label: "Dashboard", note: "Global KPIs and tool library" },
  { to: "/tools/wpa", label: "WordPress Tool", note: "Public tool landing page" },
  { to: "/tools/plugin-risk-mapper", label: "Technology Tool", note: "Plugin, release, and reliability workflows" },
  { to: "/tools/clinic-workflow-optimizer", label: "Healthcare Tool", note: "Healthcare operations and quality workflows" },
  { to: "/settings", label: "Settings", note: "Global configuration" }
];

function Protected({ children }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  return children;
}

function LoginGate() {
  const location = useLocation();
  const next = encodeURIComponent(location.pathname + location.search);
  return <Navigate to={`/login?next=${next}`} replace />;
}

function PublicLayout({ children }) {
  const { user, isAuthenticated, logout } = useAuth();

  return (
    <div className="portal-shell">
      <PortalHeader user={user} authenticated={isAuthenticated} onLogout={logout} />
      <div className="public-main">{children}</div>
      <ScrollTopButton />
      <SiteFooter />
    </div>
  );
}

function PortalLayout({
  children,
  rightRailTitle,
  rightRailSubtitle,
  rightItems,
  rightExtra,
  showRightRail = true,
  inWorkspace = false,
  onExitWorkspace,
  workspaceTopRow = null
}) {
  const { user, logout } = useAuth();
  const [portalHeaderHeight, setPortalHeaderHeight] = useState(0);
  const [workspaceTopRowHeight, setWorkspaceTopRowHeight] = useState(0);

  useEffect(() => {
    if (!workspaceTopRow) {
      setPortalHeaderHeight(0);
      setWorkspaceTopRowHeight(0);
      return undefined;
    }

    const measureLayout = () => {
      const headerNode = document.querySelector(".portal-header");
      const workspaceRowNode = document.querySelector(".workspace-top-shell-outer");
      setPortalHeaderHeight(Number(headerNode?.offsetHeight || 0));
      setWorkspaceTopRowHeight(Number(workspaceRowNode?.offsetHeight || 0));
    };

    measureLayout();
    window.addEventListener("resize", measureLayout);
    return () => window.removeEventListener("resize", measureLayout);
  }, [workspaceTopRow]);

  return (
    <div
      className={`portal-shell ${workspaceTopRow ? "has-fixed-workspace-row" : ""}`}
      style={workspaceTopRow ? {
        "--workspace-fixed-top": `${portalHeaderHeight}px`,
        "--workspace-row-height": `${workspaceTopRowHeight}px`
      } : undefined}
    >
      <PortalHeader user={user} authenticated onLogout={logout} inWorkspace={inWorkspace} onExitWorkspace={onExitWorkspace} />
      {workspaceTopRow}
      <div className={`portal-main ${showRightRail ? "" : "no-rail"}`}>
        <div className="portal-content">{children}</div>
        {showRightRail ? (
          <RightNavPanel title={rightRailTitle} subtitle={rightRailSubtitle} items={rightItems} extra={rightExtra} />
        ) : null}
      </div>
      <ScrollTopButton />
      <SiteFooter />
    </div>
  );
}

function GlobalLayout({ children, showRightRail = true }) {
  return (
    <PortalLayout
      rightRailTitle="Global Navigation"
      rightRailSubtitle="Global menus apply across all tools."
      rightItems={portalAreaItems}
      showRightRail={showRightRail}
    >
      {children}
    </PortalLayout>
  );
}

function ScrollTopButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 520);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!visible) return null;

  return (
    <button
      type="button"
      className="scroll-top-fab"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Scroll to top"
      title="Scroll to top"
    >
      ↑
    </button>
  );
}

function StageIcon({ stagePath }) {
  if (stagePath === "/tools/wpa/dashboard/incidents") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 3 2.5 20h19L12 3zm0 5.4 5.3 9.1H6.7L12 8.4zm-1 2.6v4h2v-4h-2zm0 5.2v1.8h2v-1.8h-2z" />
      </svg>
    );
  }
  if (stagePath === "/tools/wpa/dashboard/reports") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 20h16v2H2V3h2v17zm3-1V9h3v10H7zm5 0V5h3v14h-3zm5 0v-7h3v7h-3z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 3h16v18H4V3zm2 2v14h12V5H6zm2 2h8v2H8V7zm0 4h8v2H8v-2zm0 4h5v2H8v-2z" />
    </svg>
  );
}

function EnterWordPressWorkspace() {
  const { websites, activeWebsiteId, setActiveWebsite } = useWebsite();

  if (activeWebsiteId) {
    return <Navigate to="/tools/wpa/dashboard/questionnaire" replace />;
  }

  if (websites.length > 0) {
    setActiveWebsite(websites[0].id);
    return <Navigate to="/tools/wpa/dashboard/questionnaire" replace />;
  }

  return <Navigate to="/tools/wpa" replace />;
}

function WebsiteWorkspaceLayout({ children, activeWebsiteId, showReportsActions = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { websites, setActiveWebsite, activeWebsite } = useWebsite();
  const activeWorkspaceItem = useMemo(
    () => websiteNavItems.find((item) => item.to === location.pathname) || websiteNavItems[0],
    [location.pathname]
  );
  const activeWorkspaceIndex = useMemo(
    () => websiteNavItems.findIndex((item) => item.to === location.pathname),
    [location.pathname]
  );
  const isReportsStage = location.pathname === "/tools/wpa/dashboard/reports";
  const isIncidentsStage = location.pathname === "/tools/wpa/dashboard/incidents";
  const stageToneClass = location.pathname === "/tools/wpa/dashboard/reports"
    ? "stage-tone-reports"
    : location.pathname === "/tools/wpa/dashboard/incidents"
      ? "stage-tone-incidents"
      : "stage-tone-discussion";
  const [reportsTab, setReportsTab] = useState("composer");
  const [incidentsTab, setIncidentsTab] = useState("capture");

  useEffect(() => {
    if (!isReportsStage) {
      setReportsTab("composer");
    }
  }, [isReportsStage]);

  useEffect(() => {
    if (!isIncidentsStage) {
      setIncidentsTab("capture");
    }
  }, [isIncidentsStage]);

  useEffect(() => {
    const onReportsTab = (event) => {
      const next = event?.detail?.tab;
      if (next === "composer" || next === "evidence") {
        setReportsTab(next);
      }
    };
    window.addEventListener("wpa:reports-active-tab", onReportsTab);
    return () => window.removeEventListener("wpa:reports-active-tab", onReportsTab);
  }, []);

  useEffect(() => {
    const onIncidentsTab = (event) => {
      const next = event?.detail?.tab;
      if (next === "capture" || next === "preview") {
        setIncidentsTab(next);
      }
    };
    window.addEventListener("wpa:incidents-active-tab", onIncidentsTab);
    return () => window.removeEventListener("wpa:incidents-active-tab", onIncidentsTab);
  }, []);

  const handleWebsiteChange = (nextId) => {
    setActiveWebsite(nextId);
    if (nextId) {
      navigate("/tools/wpa/dashboard/questionnaire");
    } else {
      navigate("/tools/wpa");
    }
  };

  const goPrevStage = () => {
    const nextIndex = Math.max(0, activeWorkspaceIndex - 1);
    const next = websiteNavItems[nextIndex];
    if (next?.to && next.to !== location.pathname) {
      navigate(next.to);
    }
  };

  const goNextStage = () => {
    const nextIndex = Math.min(websiteNavItems.length - 1, activeWorkspaceIndex + 1);
    const next = websiteNavItems[nextIndex];
    if (next?.to && next.to !== location.pathname) {
      navigate(next.to);
    }
  };

  if (!activeWebsiteId) {
    return <Navigate to="/tools/wpa" replace />;
  }

  const workspaceHeaderRow = (
    <TopBar
      title={`Website Interface: ${activeWebsite?.name || "Selected Website"}`}
      subtitle="Focused interface for selected website. Exit from top-right anytime."
      websites={websites}
      activeWebsiteId={activeWebsiteId}
      onChangeWebsite={handleWebsiteChange}
      showWebsitePicker
      showReportsActions={showReportsActions}
    />
  );

  return (
    <PortalLayout
      inWorkspace
      onExitWorkspace={() => navigate("/tools/wpa")}
      showRightRail={false}
      workspaceTopRow={workspaceHeaderRow}
    >
      <section className="workspace-stage-shell">
        <div className={`workspace-stage-head ${stageToneClass}`}>
          <div className="workspace-stage-nav-compact">
            <button
              type="button"
              className="stage-nav-arrow"
              onClick={goPrevStage}
              disabled={activeWorkspaceIndex <= 0}
              aria-label="Previous workspace stage"
              title="Previous stage"
            >
              {"<"}
            </button>
            <div className="stage-nav-main">
              <div className="stage-nav-left">
                <span className="stage-nav-title-icon" aria-hidden="true">
                  <StageIcon stagePath={location.pathname} />
                </span>
                <p className="stage-nav-title">{String(activeWorkspaceItem.label || "").toUpperCase()}</p>
              </div>
              {isReportsStage ? (
                <div className="workspace-stage-head-right">
                  <div className="tab-row workspace-reports-tab-row">
                    <button
                      type="button"
                      className={`tab-btn ${reportsTab === "composer" ? "active" : ""}`}
                      onClick={() => {
                        setReportsTab("composer");
                        window.dispatchEvent(new CustomEvent("wpa:reports-tab-change-request", { detail: { tab: "composer" } }));
                      }}
                    >
                      Compose
                    </button>
                    <button
                      type="button"
                      className={`tab-btn ${reportsTab === "evidence" ? "active" : ""}`}
                      onClick={() => {
                        setReportsTab("evidence");
                        window.dispatchEvent(new CustomEvent("wpa:reports-tab-change-request", { detail: { tab: "evidence" } }));
                      }}
                    >
                      Evidence Hub
                    </button>
                  </div>
                </div>
              ) : null}
              {isIncidentsStage ? (
                <div className="workspace-stage-head-right">
                  <div className="tab-row workspace-reports-tab-row">
                    <button
                      type="button"
                      className={`tab-btn ${incidentsTab === "capture" ? "active" : ""}`}
                      onClick={() => {
                        setIncidentsTab("capture");
                        window.dispatchEvent(new CustomEvent("wpa:incidents-tab-change-request", { detail: { tab: "capture" } }));
                      }}
                    >
                      Capture Incident
                    </button>
                    <button
                      type="button"
                      className={`tab-btn ${incidentsTab === "preview" ? "active" : ""}`}
                      onClick={() => {
                        setIncidentsTab("preview");
                        window.dispatchEvent(new CustomEvent("wpa:incidents-tab-change-request", { detail: { tab: "preview" } }));
                      }}
                    >
                      Preview
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className="stage-nav-arrow"
              onClick={goNextStage}
              disabled={activeWorkspaceIndex >= websiteNavItems.length - 1}
              aria-label="Next workspace stage"
              title="Next stage"
            >
              {">"}
            </button>
          </div>
        </div>
        <div className="workspace-stage-content">{children}</div>
      </section>
    </PortalLayout>
  );
}

function PublicRoutes() {
  return (
    <Routes>
      <Route path="/" element={<PublicLayout><LandingPage /></PublicLayout>} />
      <Route path="/home" element={<Navigate to="/" replace />} />
      <Route path="/login" element={<PublicLayout><LoginPage /></PublicLayout>} />
      <Route path="/reports/:slug" element={<PublicLayout><PublicReportPage /></PublicLayout>} />
      <Route path="/tools/wpa" element={<PublicLayout><WordPressToolLandingPage /></PublicLayout>} />
      <Route path="/tools/:toolSlug" element={<PublicLayout><ToolPublicLandingPage /></PublicLayout>} />
      <Route path="/tools/:toolSlug/dashboard" element={<LoginGate />} />
      <Route path="/tools/wpa/dashboard/*" element={<LoginGate />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AuthenticatedRoutes({ activeWebsiteId }) {
  return (
    <Routes>
      <Route path="/" element={<GlobalLayout showRightRail={false}><LandingPage authenticated /></GlobalLayout>} />
      <Route path="/home" element={<Navigate to="/" replace />} />
      <Route
        path="/dashboard"
        element={
          <Protected>
            <GlobalLayout>
              <DashboardPage />
            </GlobalLayout>
          </Protected>
        }
      />
      <Route
        path="/settings"
        element={
          <Protected>
            <GlobalLayout>
              <SettingsPage />
            </GlobalLayout>
          </Protected>
        }
      />
      <Route path="/tools/wpa" element={<GlobalLayout showRightRail={false}><WordPressToolLandingPage /></GlobalLayout>} />
      <Route path="/reports/:slug" element={<GlobalLayout showRightRail={false}><PublicReportPage /></GlobalLayout>} />
      <Route path="/tools/:toolSlug" element={<GlobalLayout showRightRail={false}><ToolPublicLandingPage /></GlobalLayout>} />
      <Route
        path="/tools/wpa/dashboard"
        element={
          <Protected>
            <EnterWordPressWorkspace />
          </Protected>
        }
      />
      <Route
        path="/tools/wpa/selector"
        element={
          <Protected>
            <GlobalLayout showRightRail={false}>
              <WordPressToolPage />
            </GlobalLayout>
          </Protected>
        }
      />
      <Route path="/tools/wpaudit" element={<Navigate to="/tools/wpa/dashboard" replace />} />
      <Route path="/tools/wpaudit/workspace" element={<Navigate to="/tools/wpa/dashboard/questionnaire" replace />} />
      <Route
        path="/tools/:toolSlug/dashboard"
        element={
          <Protected>
            <GlobalLayout>
              <ToolWorkspacePlaceholderPage />
            </GlobalLayout>
          </Protected>
        }
      />

      <Route
        path="/tools/wpa/dashboard/questionnaire"
        element={
          <Protected>
            <WebsiteWorkspaceLayout activeWebsiteId={activeWebsiteId} showReportsActions>
              <QuestionnairePage websiteId={activeWebsiteId} />
            </WebsiteWorkspaceLayout>
          </Protected>
        }
      />
      <Route
        path="/tools/wpa/dashboard/incidents"
        element={
          <Protected>
            <WebsiteWorkspaceLayout activeWebsiteId={activeWebsiteId} showReportsActions>
              <IncidentsPage websiteId={activeWebsiteId} />
            </WebsiteWorkspaceLayout>
          </Protected>
        }
      />
      <Route
        path="/tools/wpa/dashboard/preview"
        element={
          <Protected>
            <WebsiteWorkspaceLayout activeWebsiteId={activeWebsiteId} showReportsActions>
              <PreviewPage websiteId={activeWebsiteId} />
            </WebsiteWorkspaceLayout>
          </Protected>
        }
      />
      <Route
        path="/tools/wpa/dashboard/reports"
        element={
          <Protected>
            <WebsiteWorkspaceLayout activeWebsiteId={activeWebsiteId} showReportsActions>
              <ReportsPage websiteId={activeWebsiteId} />
            </WebsiteWorkspaceLayout>
          </Protected>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return (
      <MetaProvider>
        <PublicRoutes />
      </MetaProvider>
    );
  }

  return (
    <MetaProvider>
      <WebsiteProvider>
        <PrivateRouter />
      </WebsiteProvider>
    </MetaProvider>
  );
}

function PrivateRouter() {
  const { activeWebsiteId } = useWebsite();
  return <AuthenticatedRoutes activeWebsiteId={activeWebsiteId} />;
}
